// Utilidad de normalización para stock requests y transfer requests
// Enfoque: limpiar items duplicados, trim de códigos, asegurar cantidades positivas, colapsar items repetidos

export interface NormalizableItem {
  item_code?: string
  code?: string
  requested_quantity?: number
  quantity?: number
  transfer_quantity?: number
  approved_quantity?: number
  [k: string]: any
}

export interface NormalizedResult<T=any> {
  payload: T
  itemsBefore: number
  itemsAfter: number
  removedZeroOrInvalid: number
  mergedDuplicates: number
}

function normalizeItems(rawItems: NormalizableItem[] = []): { items: NormalizableItem[]; stats: Omit<NormalizedResult,'payload'> } {
  const stats = { itemsBefore: rawItems.length, itemsAfter: 0, removedZeroOrInvalid: 0, mergedDuplicates: 0 }
  const map = new Map<string, NormalizableItem>()

  for (const it of rawItems) {
    if (!it) { stats.removedZeroOrInvalid++; continue }
    const codeRaw = (it.item_code || it.code || '').trim()
    if (!codeRaw) { stats.removedZeroOrInvalid++; continue }
    const key = codeRaw.toUpperCase()
    // Determinar campo de cantidad principal (prioridad)
    const qty = it.requested_quantity ?? it.transfer_quantity ?? it.quantity ?? it.approved_quantity
    if (typeof qty !== 'number' || !isFinite(qty) || qty <= 0) { stats.removedZeroOrInvalid++; continue }
    if (map.has(key)) {
      const existing = map.get(key)!
      const existingQty = existing.requested_quantity ?? existing.transfer_quantity ?? existing.quantity ?? existing.approved_quantity ?? 0
      const newQty = existingQty + qty
      // Mantener campo original si coincide, si no utilizar requested_quantity por defecto
      if (typeof existing.requested_quantity === 'number' || typeof it.requested_quantity === 'number') {
        existing.requested_quantity = newQty
      } else if (typeof existing.transfer_quantity === 'number' || typeof it.transfer_quantity === 'number') {
        existing.transfer_quantity = newQty
      } else if (typeof existing.quantity === 'number' || typeof it.quantity === 'number') {
        existing.quantity = newQty
      } else if (typeof existing.approved_quantity === 'number' || typeof it.approved_quantity === 'number') {
        existing.approved_quantity = newQty
      } else {
        existing.requested_quantity = newQty
      }
      stats.mergedDuplicates++
    } else {
      const clone: NormalizableItem = { ...it }
      clone.item_code = codeRaw
      if (typeof clone.requested_quantity !== 'number' && typeof clone.transfer_quantity !== 'number' && typeof clone.quantity !== 'number' && typeof clone.approved_quantity !== 'number') {
        clone.requested_quantity = qty
      }
      map.set(key, clone)
    }
  }

  const items = Array.from(map.values())
  stats.itemsAfter = items.length
  return { items, stats }
}

export function normalizeInventoryRequest<T extends { items?: NormalizableItem[] }>(payload: T): NormalizedResult<T> {
  if (!payload || typeof payload !== 'object') {
    return { payload, itemsBefore: 0, itemsAfter: 0, removedZeroOrInvalid: 0, mergedDuplicates: 0 }
  }
  const { items: rawItems } = payload
  if (!Array.isArray(rawItems)) {
    return { payload, itemsBefore: 0, itemsAfter: 0, removedZeroOrInvalid: 0, mergedDuplicates: 0 }
  }
  const { items, stats } = normalizeItems(rawItems)
  ;(payload as any).items = items
  return { payload, ...stats }
}

export function withNormalized<T extends { items?: NormalizableItem[] }>(payload: T, label = 'inventory.normalize'): T {
  const { itemsBefore, itemsAfter, removedZeroOrInvalid, mergedDuplicates } = normalizeInventoryRequest(payload)
  try {
    // eslint-disable-next-line no-console
    console.log(`[${label}] itemsBefore=${itemsBefore} itemsAfter=${itemsAfter} removed=${removedZeroOrInvalid} mergedDup=${mergedDuplicates}`)
  } catch {}
  return payload
}
